import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useTimeEntries } from "@/hooks/useTimeEntries";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/types/database.types";

type TimeEntryInsert = Database["public"]["Tables"]["time_entries"]["Insert"];

const schema = z
  .object({
    date: z.string().min(1, "Date is required"),
    startTime: z.string().min(1, "Start time is required"),
    endTime: z.string().min(1, "End time is required"),
    description: z.string().optional(),
    billable: z.boolean(),
  })
  .refine(
    (data) => {
      // Validate that end time is after start time
      const start = new Date(`${data.date}T${data.startTime}`);
      const end = new Date(`${data.date}T${data.endTime}`);
      return end > start;
    },
    {
      message: "End time must be after start time",
      path: ["endTime"],
    },
  );

type FormValues = z.infer<typeof schema>;

interface TimeEntryFormProps {
  taskId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function TimeEntryForm({ taskId, onSuccess, onCancel }: TimeEntryFormProps) {
  const { user } = useAuth();
  const { createEntry } = useTimeEntries(taskId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      date: new Date().toISOString().split("T")[0],
      startTime: "09:00",
      endTime: "17:00",
      description: "",
      billable: true,
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (!user) {
      setError("You must be logged in");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startDateTime = new Date(`${values.date}T${values.startTime}`);
      const endDateTime = new Date(`${values.date}T${values.endTime}`);

      const entry: TimeEntryInsert = {
        task_id: taskId,
        user_id: user.id,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        description: values.description || null,
        billable: values.billable,
      };

      await createEntry(entry);
      form.reset();
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create time entry";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      {error && <div className="text-sm text-destructive">{error}</div>}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="date">Date</Label>
          <Input id="date" type="date" {...form.register("date")} disabled={loading} />
          {form.formState.errors.date && (
            <p className="text-xs text-destructive">{form.formState.errors.date.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="billable">Billable</Label>
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="billable"
              checked={form.watch("billable")}
              onCheckedChange={(checked) => form.setValue("billable", checked === true)}
              disabled={loading}
            />
            <Label htmlFor="billable" className="font-normal cursor-pointer">
              Mark as billable
            </Label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="startTime">Start Time</Label>
          <Input id="startTime" type="time" {...form.register("startTime")} disabled={loading} />
          {form.formState.errors.startTime && (
            <p className="text-xs text-destructive">{form.formState.errors.startTime.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="endTime">End Time</Label>
          <Input id="endTime" type="time" {...form.register("endTime")} disabled={loading} />
          {form.formState.errors.endTime && (
            <p className="text-xs text-destructive">{form.formState.errors.endTime.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          {...form.register("description")}
          disabled={loading}
          rows={3}
          placeholder="Add notes about this time entry..."
        />
      </div>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={loading}>
          {loading ? "Adding..." : "Add Time Entry"}
        </Button>
      </div>
    </form>
  );
}